import { useCallback } from 'react';
import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import communityAPI from '../api';
import { normalizeListResponse } from '../api/normalizers';

const buildQueryKey = (postId) => ['community', 'comments', { postId }];

export const useComments = (postId, options = {}) => {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const resolvedEnabled = Boolean(postId) && enabled;

  const listQuery = useQuery({
    queryKey: buildQueryKey(postId),
    enabled: resolvedEnabled,
    queryFn: async () => {
      const response = await communityAPI.comments.getByPost(postId);
      const normalized = normalizeListResponse(response.data);
      return normalized.results;
    },
    staleTime: 15_000,
    onError: () => {
      message.error('Failed to fetch comments');
    }
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: buildQueryKey(postId) });
  }, [postId, queryClient]);

  const createMutation = useMutation({
    mutationFn: (data) =>
      communityAPI.comments.create({
        ...data,
        post: postId
      }),
    onSuccess: async () => {
      message.success('Comment added successfully');
      await invalidate();
    },
    onError: () => {
      message.error('Failed to add comment');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => communityAPI.comments.update(id, data),
    onSuccess: async () => {
      message.success('Comment updated successfully');
      await invalidate();
    },
    onError: () => {
      message.error('Failed to update comment');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => communityAPI.comments.delete(id),
    onSuccess: async () => {
      message.success('Comment deleted successfully');
      await invalidate();
    },
    onError: () => {
      message.error('Failed to delete comment');
    }
  });

  const toggleLikeMutation = useMutation({
    mutationFn: (id) => communityAPI.comments.toggleLike(id),
    onError: () => {
      message.error('Failed to toggle like');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: buildQueryKey(postId) });
    }
  });

  return {
    comments: listQuery.data ?? [],
    loading: listQuery.isPending,
    fetchComments: listQuery.refetch,
    createComment: async (data) => {
      const response = await createMutation.mutateAsync(data);
      return response?.data;
    },
    updateComment: async (id, data) => {
      const response = await updateMutation.mutateAsync({ id, data });
      return response?.data;
    },
    deleteComment: (id) => deleteMutation.mutateAsync(id),
    toggleLike: async (id) => {
      const response = await toggleLikeMutation.mutateAsync(id);
      return response?.data;
    }
  };
};

export default useComments;
